import { useState, useEffect, useRef, lazy, Suspense, Fragment, memo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, ChevronLeft, ChevronRight, Loader2, X, FileText } from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/hooks/useAuth';
import { apiFetch, API_PREFIX, getAccessToken } from '@/lib/api-client';
import type {
  EnrichmentAddressStatus,
  EnrichmentNetworkDetails,
  DmpOrderResponse,
  EnrichmentSubscription,
  OrderWorkflowEntry,
  DeepomaticJob,
} from '@uniticket/shared';

const AddressInfoMap = lazy(() => import('./AddressInfoMap'));

// Fetches an image with auth headers (plain <img src> doesn't send Bearer token).
const AuthImg = memo(({ src, alt, className, onError, style, onClick, onWheel }: {
  src: string; alt: string; className?: string; onError?: () => void;
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler<HTMLImageElement>;
  onWheel?: React.WheelEventHandler<HTMLImageElement>;
}) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    const token = getAccessToken();
    fetch(src, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.ok ? r.blob() : Promise.reject())
      .then((blob) => { if (active) setObjectUrl(URL.createObjectURL(blob)); })
      .catch(() => { if (active) onError?.(); });
    return () => {
      active = false;
      setObjectUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    };
  }, [src]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!objectUrl) return <div className={className} style={{ background: '#f1f5f9' }} />;
  return <img className={className} src={objectUrl} alt={alt} style={style} onClick={onClick} onWheel={onWheel} />;
});

// ── Local interfaces (not in shared — experiment-only) ───────────────────────

interface AddressSearchRow {
  jvid: number;
  municipality: string | null;
  city: string | null;
  postal_code: string | null;
  street_name: string | null;
  house_number_extension: string | null;
  bus_number: string | null;
}

interface SsvTsaRow {
  task: string | null;
  ssvStatus: string | null;
  tsaStatus: string | null;
  ssv_doc_token: string | null;
  tsa_doc_for_signature_token: string | null;
  tsa_doc_signed_token: string | null;
}

interface JvidData {
  info: EnrichmentAddressStatus | null;
  network: EnrichmentNetworkDetails[];
  orders: DmpOrderResponse[];
  subscriptions: EnrichmentSubscription[];
  ssvTsa: SsvTsaRow[];
}

// ── SharePoint document types ────────────────────────────────────────────────

interface DriveFile {
  id: string;
  name: string;
  driveId?: string;
  itemId?: string;
  size?: number;
  lastModified?: string;
  parentPath?: string;
  hasThumbnail?: boolean;
  site?: string | null;
  hash?: string | null;
}

interface FolderSection {
  name: string;
  driveId?: string;
  itemId: string;
  files: DriveFile[];
}

interface SearchHit {
  id: string;
  name: string;
  parentPath?: string;
  summary?: string;
  size?: number;
  lastModified?: string;
  driveId?: string;
  itemId?: string;
  site?: string | null;
  hash?: string | null;
  sections?: FolderSection[];
}

interface LightboxState {
  kind: 'image' | 'iframe';
  src: string;
  name: string;
  // Sibling images from the same grid, for prev/next navigation. Only
  // populated for kind:'image' openings — iframe (PDF/Office) tiles don't
  // get gallery navigation.
  gallery?: { src: string; name: string }[];
  index?: number;
}

// ── Order-image Azure Blob types ──────────────────────────────────────────────

interface AzureFile {
  path: string;
  name: string;
  // Real EXIF capture date — null (no fallback to upload/migration date) when
  // the file has no EXIF DateTimeOriginal/CreateDate.
  takenAt: string | null;
}

interface AzureSection {
  name: string; // "orderId / timestamp" breadcrumb
  orderId: string;
  files: AzureFile[];
}

// ── File helpers — copied verbatim from HC AddressSearchView.jsx ─────────────

const EXT_ICONS: Record<string, string> = {
  xlsx: '📊', xls: '📊', csv: '📊',
  docx: '📝', doc: '📝', rtf: '📝',
  pdf: '📕',
  pptx: '📽️', ppt: '📽️',
  png: '🖼️', jpg: '🖼️', jpeg: '🖼️', jfif: '🖼️', gif: '🖼️', svg: '🖼️',
  zip: '📦', rar: '📦', '7z': '📦',
  txt: '📃', md: '📃', log: '📃',
  msg: '📧', eml: '📧',
};
const IMAGE_EXTS = new Set(['png', 'jpg', 'jpeg', 'jfif', 'gif', 'webp', 'svg', 'bmp']);
const TEXT_EXTS = new Set(['txt', 'md', 'log', 'csv', 'json', 'xml', 'yaml', 'yml']);
const OFFICE_EXTS = new Set(['xlsx', 'xls', 'docx', 'doc', 'pptx', 'ppt', 'odt', 'ods', 'odp', 'rtf']);
function extOf(name: string | undefined | null): string | null {
  const m = name?.match(/\.([a-z0-9]{2,4})$/i);
  return m ? m[1].toLowerCase() : null;
}
function isImage(name: string | undefined | null) { return IMAGE_EXTS.has(extOf(name) ?? ''); }
function isPdf(name: string | undefined | null) { return extOf(name) === 'pdf'; }
function isText(name: string | undefined | null) { return TEXT_EXTS.has(extOf(name) ?? ''); }
function isOffice(name: string | undefined | null) { return OFFICE_EXTS.has(extOf(name) ?? ''); }
function iconFor(name: string | undefined | null): string {
  if (!name) return '📄';
  const m = name.match(/\.([a-z0-9]{2,4})$/i);
  if (!m) return '📁';
  return EXT_ICONS[m[1].toLowerCase()] || '📄';
}

function renderSummary(html: string | undefined | null): React.ReactNode {
  if (!html) return null;
  const withEllipsis = html.replace(/<ddd\s*\/>/g, '…');
  const parts = withEllipsis.split(/(<c0>.*?<\/c0>)/g);
  return parts.map((p, i) => {
    const m = p.match(/^<c0>(.*?)<\/c0>$/);
    return m ? <mark key={i} className="bg-yellow-100 text-yellow-900 rounded px-0.5">{m[1]}</mark> : <span key={i}>{p}</span>;
  });
}

function formatFileSize(bytes: number | undefined): string {
  const n = Number(bytes);
  if (!n) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0; let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ', ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

const DASH = '—';

// Short status code shown alongside the raw DMP order status — mapping differs
// for CEASE orders vs every other order type.
const SHORT_STATUS_MAP: Record<string, Record<string, string>> = {
  CEASE: {
    CREATED: 'PD',
    PLANNED_BY_CONSTRUCTOR: 'PD',
    CANCELLED: 'CD',
    TECHNICALLY_EXECUTED: 'D',
    DELAYED: 'PD',
    DELIVERED: 'D',
  },
  DEFAULT: {
    CREATED: 'PP',
    PLANNED_BY_CONSTRUCTOR: 'PP',
    CANCELLED: 'CP',
    TECHNICALLY_EXECUTED: 'P',
    DELAYED: 'PP',
    DELIVERED: 'P',
  },
};

function shortOrderStatus(orderType: string | null | undefined, status: string | null | undefined): string {
  if (!status) return DASH;
  const map = orderType === 'CEASE' ? SHORT_STATUS_MAP.CEASE : SHORT_STATUS_MAP.DEFAULT;
  return map[status] ?? DASH;
}

function Row({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div className="flex gap-4 py-1.5 border-b border-slate-100 last:border-0">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 shrink-0 w-52">{label}</span>
      <span className="text-sm text-slate-700 break-words">{value != null && value !== '' ? value : DASH}</span>
    </div>
  );
}

function MetaField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-xs text-slate-700 break-words">{value ?? DASH}</div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

export function AddressInfoPage() {
  const { isExternal, user, canViewNetwork } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation('common');

  // External operators can access this page but see a limited view (no network, SSV/TSA, documents).

  // Address text search state
  const [addressQuery, setAddressQuery] = useState('');
  const [addressLoading, setAddressLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<AddressSearchRow[]>([]);
  const skipSearchRef = useRef(false);
  const suggestionsRef = useRef<HTMLUListElement>(null);

  // JVID state — set either from suggestion click, direct input, or UTAC resolution
  const [jvidInput, setJvidInput] = useState(searchParams.get('jvid') || '');
  const [jvid, setJvid] = useState(searchParams.get('jvid') || '');

  // UTAC search state
  const [utacInput, setUtacInput] = useState(searchParams.get('utac') || '');
  const [utacLoading, setUtacLoading] = useState(false);
  const [utacError, setUtacError] = useState<string | null>(null);

  // DMP data
  const [jvidLoading, setJvidLoading] = useState(false);
  const [jvidData, setJvidData] = useState<JvidData | null>(null);
  const [jvidError, setJvidError] = useState<string | null>(null);

  // Per-order workflow, lazily fetched
  const [workflows, setWorkflows] = useState<Record<number, OrderWorkflowEntry[] | 'loading' | 'error'>>({});
  const [expandedOrder, setExpandedOrder] = useState<number | null>(null);

  // SharePoint documents
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsHits, setDocsHits] = useState<SearchHit[]>([]);
  const [docsError, setDocsError] = useState<string | null>(null);

  // Deepomatic field photos (internal only)
  const [deepomaticLoading, setDeepomaticLoading] = useState(false);
  const [deepomaticJobs, setDeepomaticJobs] = useState<DeepomaticJob[]>([]);
  const [deepomaticError, setDeepomaticError] = useState<string | null>(null);
  // inputIds whose image/thumbnail failed to load (e.g. missing on Azure Blob) — hide their tile entirely, no broken-image placeholder.
  const [missingDeepomaticImages, setMissingDeepomaticImages] = useState<Set<string>>(new Set());

  // Order-image Azure Blob files (internal only)
  const [azureLoading, setAzureLoading] = useState(false);
  const [azureSections, setAzureSections] = useState<AzureSection[]>([]);
  const [azureError, setAzureError] = useState<string | null>(null);

  // Lightbox
  const [lightbox, setLightbox] = useState<LightboxState | null>(null);
  // Lightbox image zoom (click or scroll wheel, centered at the cursor)
  const [imgZoom, setImgZoom] = useState(1);
  const [imgZoomOrigin, setImgZoomOrigin] = useState('50% 50%');

  // ── Address text search debounce ──
  useEffect(() => {
    if (skipSearchRef.current) { skipSearchRef.current = false; return; }
    if (addressQuery.length < 3) { setSuggestions([]); return; }
    const id = setTimeout(async () => {
      setAddressLoading(true);
      try {
        const res = await apiFetch<{ rows: AddressSearchRow[] }>(`/address-info/search?q=${encodeURIComponent(addressQuery)}`);
        setSuggestions(res.rows || []);
      } catch { setSuggestions([]); }
      finally { setAddressLoading(false); }
    }, 300);
    return () => clearTimeout(id);
  }, [addressQuery]);

  // ── JVID input → trigger lookup when 8 digits ──
  useEffect(() => {
    if (!/^\d{8}$/.test(jvidInput)) { if (jvidInput === '') { setJvid(''); setJvidData(null); setJvidError(null); } return; }
    setJvid(jvidInput);
    setSearchParams(jvidInput ? { jvid: jvidInput } : {}, { replace: true });
  }, [jvidInput]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch DMP + docs when JVID changes ──
  useEffect(() => {
    if (!/^\d{8}$/.test(jvid)) return;
    let cancelled = false;

    setJvidLoading(true);
    setJvidData(null);
    setJvidError(null);
    setWorkflows({});
    setExpandedOrder(null);
    setDocsHits([]);
    setDocsError(null);
    setDeepomaticJobs([]);
    setDeepomaticError(null);
    setMissingDeepomaticImages(new Set());
    setAzureSections([]);
    setAzureError(null);

    apiFetch<{ info: EnrichmentAddressStatus | null; network: EnrichmentNetworkDetails[]; orders: DmpOrderResponse[]; subscriptions: EnrichmentSubscription[]; ssvTsa: SsvTsaRow[] }>(`/address-info/${jvid}`)
      .then((d) => { if (!cancelled) { setJvidData(d); setJvidLoading(false); } })
      .catch((e: Error) => { if (!cancelled) { setJvidError(e.message || 'Lookup failed'); setJvidLoading(false); } });

    setDocsLoading(true);
    apiFetch<{ hits: SearchHit[] }>(`/address-info/${jvid}/documents`)
      .then((d) => { if (!cancelled) { setDocsHits(d.hits || []); setDocsLoading(false); } })
      .catch((e: Error) => { if (!cancelled) { setDocsError(e.message || 'Document search failed'); setDocsLoading(false); } });

    // Deepomatic field photos are internal-only sensitive data — skip the
    // fetch entirely for external operators rather than fetch-and-hide.
    if (!isExternal) {
      setDeepomaticLoading(true);
      apiFetch<{ jobs: DeepomaticJob[] }>(`/address-info/${jvid}/deepomatic`)
        .then((d) => { if (!cancelled) { setDeepomaticJobs(d.jobs || []); setDeepomaticLoading(false); } })
        .catch((e: Error) => { if (!cancelled) { setDeepomaticError(e.message || 'Deepomatic lookup failed'); setDeepomaticLoading(false); } });

      setAzureLoading(true);
      apiFetch<{ sections: AzureSection[] }>(`/address-info/${jvid}/azure-files`)
        .then((d) => { if (!cancelled) { setAzureSections(d.sections || []); setAzureLoading(false); } })
        .catch((e: Error) => { if (!cancelled) { setAzureError(e.message || 'Azure file search failed'); setAzureLoading(false); } });
    }

    return () => { cancelled = true; };
  }, [jvid, isExternal]);

  // ── Lightbox prev/next within its gallery, wrapping around ──
  function navigateLightbox(delta: number) {
    setLightbox((cur) => {
      if (!cur || !cur.gallery || cur.index == null) return cur;
      const len = cur.gallery.length;
      const nextIndex = (cur.index + delta + len) % len;
      const next = cur.gallery[nextIndex];
      return { kind: 'image', src: next.src, name: next.name, gallery: cur.gallery, index: nextIndex };
    });
  }

  // ── Lightbox Esc / arrow-key handler ──
  useEffect(() => {
    if (!lightbox) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightbox(null);
      else if (e.key === 'ArrowLeft') navigateLightbox(-1);
      else if (e.key === 'ArrowRight') navigateLightbox(1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox]);

  // ── Reset zoom whenever a (new) image is opened/closed ──
  useEffect(() => {
    setImgZoom(1);
    setImgZoomOrigin('50% 50%');
  }, [lightbox?.src]);

  function handleImageZoomClick(e: React.MouseEvent<HTMLImageElement>) {
    if (imgZoom > 1) { setImgZoom(1); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setImgZoomOrigin(`${x}% ${y}%`);
    setImgZoom(2.5);
  }

  function handleImageZoomWheel(e: React.WheelEvent<HTMLImageElement>) {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setImgZoomOrigin(`${x}% ${y}%`);
    setImgZoom((z) => Math.min(4, Math.max(1, z - e.deltaY * 0.0025)));
  }

  // ── Close suggestions on outside click ──
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node)) setSuggestions([]);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function selectSuggestion(s: AddressSearchRow) {
    const label = `${s.municipality || ''} ${s.street_name || ''}${s.house_number_extension ? ' ' + s.house_number_extension : ''}${s.bus_number ? ' bus ' + s.bus_number : ''}${s.postal_code || s.city ? `, ${s.postal_code || ''} ${s.city || ''}`.trim() : ''}`.trim();
    skipSearchRef.current = true;
    setAddressQuery(label);
    setSuggestions([]);
    setJvidInput(String(s.jvid));
  }

  useEffect(() => {
    if (!/^5430002977\d{8}$/.test(utacInput)) return;
    let cancelled = false;
    setUtacError(null);
    setUtacLoading(true);
    apiFetch<{ jvid: string }>(`/address-info/utac/${encodeURIComponent(utacInput)}`)
      .then((res) => {
        if (!cancelled) {
          setJvidInput(res.jvid);
          setAddressQuery('');
          setSuggestions([]);
        }
      })
      .catch((e: any) => {
        if (!cancelled) setUtacError(e?.message?.includes('404') ? `UTAC "${utacInput}" not found` : 'UTAC lookup failed');
      })
      .finally(() => { if (!cancelled) setUtacLoading(false); });
    return () => { cancelled = true; };
  }, [utacInput]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleOrder(orderId: number) {
    setExpandedOrder((cur) => (cur === orderId ? null : orderId));
    if (workflows[orderId] === undefined) {
      setWorkflows((w) => ({ ...w, [orderId]: 'loading' }));
      apiFetch<{ rows: OrderWorkflowEntry[] }>(`/address-info/${jvid}/workflow/${orderId}`)
        .then((res) => setWorkflows((w) => ({ ...w, [orderId]: res.rows })))
        .catch(() => setWorkflows((w) => ({ ...w, [orderId]: 'error' })));
    }
  }

  // ── File tile (documents) — ported from HC AddressSearchView.jsx ──
  function renderFileTile(it: DriveFile, dupKeys: Set<string>, gallery?: { src: string; name: string }[]) {
    const icon = iconFor(it.name);
    const meta = fmtDate(it.lastModified);
    const isDup = dupKeys && it.site === 'OrderManagement' && !!it.hash && dupKeys.has(it.hash);
    const _tok = encodeURIComponent(getAccessToken() || '');
    const thumbSrc = `${API_PREFIX}/address-info/thumbnail?driveId=${encodeURIComponent(it.driveId || '')}&itemId=${encodeURIComponent(it.itemId || '')}`;
    const dlBase = `${API_PREFIX}/address-info/download?driveId=${encodeURIComponent(it.driveId || '')}&itemId=${encodeURIComponent(it.itemId || '')}&_token=${_tok}`;
    const visual = it.hasThumbnail
      ? <AuthImg className="w-full h-24 object-cover rounded" src={thumbSrc} alt={it.name} />
      : <div className="w-full h-24 flex items-center justify-center text-3xl">{icon}</div>;
    const showName = !isImage(it.name);

    const inner = (
      <div className="relative text-left">
        {isDup && <span className="absolute top-1 right-1 text-[9px] font-bold bg-amber-100 text-amber-700 rounded px-1 border border-amber-200">OM</span>}
        {visual}
        {showName && <div className="mt-1 text-xs text-slate-700 truncate font-medium">{it.name}</div>}
        <div className="text-[10px] text-slate-400">{meta}</div>
      </div>
    );

    const tileClass = `block w-36 rounded-lg border p-2 hover:shadow-sm transition-shadow cursor-pointer ${isDup ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-white'}`;

    if (isImage(it.name) && it.hasThumbnail) {
      const fullSrc = `${thumbSrc}&size=large`;
      const index = gallery?.findIndex((g) => g.src === fullSrc);
      return (
        <button key={it.id} type="button" className={tileClass}
          onClick={() => setLightbox({ kind: 'image', src: fullSrc, name: it.name, gallery, index: index != null && index >= 0 ? index : undefined })}>
          {inner}
        </button>
      );
    }
    if (isPdf(it.name)) {
      return (
        <button key={it.id} type="button" className={tileClass}
          onClick={() => setLightbox({ kind: 'iframe', src: `${dlBase}&disposition=inline`, name: it.name })}>
          {inner}
        </button>
      );
    }
    if (isText(it.name)) {
      return (
        <button key={it.id} type="button" className={tileClass}
          onClick={() => setLightbox({ kind: 'iframe', src: `${dlBase}&disposition=inline`, name: it.name })}>
          {inner}
        </button>
      );
    }
    if (isOffice(it.name)) {
      return (
        <button key={it.id} type="button" className={tileClass}
          onClick={() => setLightbox({ kind: 'iframe', src: `${dlBase}&disposition=inline&format=pdf`, name: it.name })}>
          {inner}
        </button>
      );
    }
    return (
      <a key={it.id} className={tileClass} href={dlBase}>
        {inner}
      </a>
    );
  }

  // ── Photo tile (Deepomatic) — adapted from azure-viewer's WorkItemCard ──
  function renderDeepomaticTile(item: DeepomaticJob['workItems'][number], gallery?: { src: string; name: string }[]) {
    // Missing on Azure Blob (or any other load failure) — don't show a broken tile at all.
    if (missingDeepomaticImages.has(item.inputId)) return null;

    const filename = `${item.inputId}.${item.fileExtension || 'jpg'}`;
    const thumbSrc = `${API_PREFIX}/address-info/deepomatic/thumbnail/${encodeURIComponent(filename)}`;
    const fullSrc = `${API_PREFIX}/address-info/deepomatic/image/${encodeURIComponent(filename)}`;
    const label = item.workItemName || item.inputId;
    const index = gallery?.findIndex((g) => g.src === fullSrc);
    return (
      <button
        key={item.inputId}
        type="button"
        className="block w-44 rounded-lg border border-slate-200 bg-white p-2 hover:shadow-sm transition-shadow cursor-pointer text-left"
        onClick={() => setLightbox({ kind: 'image', src: fullSrc, name: label, gallery, index: index != null && index >= 0 ? index : undefined })}
      >
        <AuthImg
          className="w-full h-28 object-cover rounded"
          src={thumbSrc}
          alt={label}
          onError={() => setMissingDeepomaticImages((prev) => new Set(prev).add(item.inputId))}
        />
        <div className="mt-1 text-xs text-slate-700 truncate font-medium">{label}</div>
        <div className="mt-1 flex items-center gap-1 flex-wrap">
          {item.conformityOk != null && (
            <span className={`text-[9px] font-semibold rounded px-1 border ${item.conformityOk ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-600 border-red-200'}`}>
              {item.conformityOk ? 'Conform' : 'Non-conform'}
            </span>
          )}
          {item.hasDuplicate && (
            <span className="text-[9px] font-semibold rounded px-1 border bg-purple-50 text-purple-600 border-purple-200">Duplicate</span>
          )}
        </div>
        <div className="mt-0.5 text-[10px] text-slate-400">{fmtDateTime(item.inputCreateDate)}</div>
      </button>
    );
  }

  // ── Azure file tile — same branching as renderFileTile(), minus the
  // SharePoint-only cross-site dedup badge (no hash/site on Azure files). ──
  function renderAzureFileTile(f: AzureFile, gallery?: { src: string; name: string }[]) {
    const icon = iconFor(f.name);
    const thumbSrc = `${API_PREFIX}/address-info/azure-thumbnail?path=${encodeURIComponent(f.path)}`;
    const fullSrc = `${API_PREFIX}/address-info/azure-image?path=${encodeURIComponent(f.path)}`;
    const dlHref = `${fullSrc}&_token=${encodeURIComponent(getAccessToken() || '')}`;
    const showName = !isImage(f.name);
    const index = gallery?.findIndex((g) => g.src === fullSrc);

    const inner = (
      <div className="text-left">
        {isImage(f.name)
          ? <AuthImg className="w-full h-24 object-cover rounded" src={thumbSrc} alt={f.name} />
          : <div className="w-full h-24 flex items-center justify-center text-3xl">{icon}</div>}
        {showName && <div className="mt-1 text-xs text-slate-700 truncate font-medium">{f.name}</div>}
        {f.takenAt && <div className="text-[10px] text-slate-400">{fmtDate(f.takenAt)}</div>}
      </div>
    );

    const tileClass = 'block w-36 rounded-lg border border-slate-200 bg-white p-2 hover:shadow-sm transition-shadow cursor-pointer';

    if (isImage(f.name)) {
      return (
        <button key={f.path} type="button" className={tileClass}
          onClick={() => setLightbox({ kind: 'image', src: fullSrc, name: f.name, gallery, index: index != null && index >= 0 ? index : undefined })}>
          {inner}
        </button>
      );
    }
    if (isPdf(f.name) || isText(f.name)) {
      return (
        <button key={f.path} type="button" className={tileClass}
          onClick={() => setLightbox({ kind: 'iframe', src: dlHref, name: f.name })}>
          {inner}
        </button>
      );
    }
    return (
      <a key={f.path} className={tileClass} href={dlHref}>
        {inner}
      </a>
    );
  }

  // ── Document cross-site dedup logic — ported from HC AddressSearchView.jsx ──
  function buildVisibleHits(hits: SearchHit[]): { visibleHits: SearchHit[]; dupKeys: Set<string> } {
    const isFolderHit = (h: SearchHit) => !h.name || !/\.[a-z0-9]{2,4}$/i.test(h.name);

    const byKey = new Map<string, Set<string>>();
    for (const h of hits) {
      // Top-level file hits (e.g. PDFs returned directly from search)
      if (!isFolderHit(h) && h.hash) {
        if (!byKey.has(h.hash)) byKey.set(h.hash, new Set());
        byKey.get(h.hash)!.add(h.site || '');
      }
      // Files nested inside folder sections
      for (const s of (h.sections || [])) {
        for (const f of (s.files || [])) {
          if (!f.hash) continue;
          if (!byKey.has(f.hash)) byKey.set(f.hash, new Set());
          byKey.get(f.hash)!.add(f.site || '');
        }
      }
    }
    const dupKeys = new Set<string>();
    for (const [k, sites] of byKey) {
      if (sites.has('OrderManagement') && sites.has('UnifiberOperators')) dupKeys.add(k);
    }

    const hitHasVisibleContent = (h: SearchHit) =>
      (h.sections || []).some((s) => (s.files || []).some((f) => !(f.site === 'OrderManagement' && f.hash && dupKeys.has(f.hash))));

    const filtered = hits.filter((h) => {
      if (!isFolderHit(h)) {
        // Hide the OrderManagement copy of a file that also exists in UnifiberOperators
        if (h.site === 'OrderManagement' && h.hash && dupKeys.has(h.hash)) return false;
        return true;
      }
      if (h.site === 'OrderManagement' && !hitHasVisibleContent(h)) return false;
      return true;
    });

    // Merge folder hits sharing the same name.
    const groups = new Map<string, SearchHit[]>();
    const order: (SearchHit | string)[] = [];
    for (const h of filtered) {
      if (!isFolderHit(h)) { order.push(h); continue; }
      if (!groups.has(h.name)) { groups.set(h.name, []); order.push(h.name); }
      groups.get(h.name)!.push(h);
    }
    const merged = order.map((key) => {
      if (typeof key !== 'string') return key;
      const group = groups.get(key)!;
      if (group.length === 1) return group[0];
      const base = group.find((h) => h.site === 'UnifiberOperators') || group[0];
      const sectionMap = new Map<string, FolderSection>();
      for (const h of group) {
        for (const s of (h.sections || [])) {
          if (!sectionMap.has(s.name)) sectionMap.set(s.name, { ...s, files: [] });
          const ms = sectionMap.get(s.name)!;
          for (const f of (s.files || [])) {
            if (!f.hash || !ms.files.some((mf) => mf.hash === f.hash)) ms.files.push(f);
          }
        }
      }
      return { ...base, sections: [...sectionMap.values()] };
    });

    // Final pass: true duplicate files (same hash) anywhere in the result —
    // whether cross-site or within the same folder — keep only the earliest
    // by lastModified (the first picture taken) and drop the rest, mirroring
    // the same "keep earliest of a confirmed duplicate" rule used for Azure
    // files. Distinct from dupKeys above, which only flags cross-site
    // OrderManagement/UnifiberOperators pairs for the "OM" badge.
    const survivorIdByHash = new Map<string, string>();
    const byHashAll = new Map<string, Array<{ id: string; lastModified?: string }>>();
    const collect = (f: { id: string; hash?: string | null; lastModified?: string }) => {
      if (!f.hash) return;
      if (!byHashAll.has(f.hash)) byHashAll.set(f.hash, []);
      byHashAll.get(f.hash)!.push({ id: f.id, lastModified: f.lastModified });
    };
    for (const h of merged) {
      if (!isFolderHit(h)) collect(h);
      for (const s of (h.sections || [])) {
        for (const f of (s.files || [])) collect(f);
      }
    }
    for (const [hash, entries] of byHashAll) {
      if (entries.length < 2) continue;
      const earliest = [...entries].sort((a, b) => {
        const ta = a.lastModified ? new Date(a.lastModified).getTime() : Infinity;
        const tb = b.lastModified ? new Date(b.lastModified).getTime() : Infinity;
        return ta - tb;
      })[0];
      survivorIdByHash.set(hash, earliest.id);
    }
    const keep = (f: { id: string; hash?: string | null }) =>
      !f.hash || !survivorIdByHash.has(f.hash) || survivorIdByHash.get(f.hash) === f.id;

    const deduped = merged
      .filter((h) => isFolderHit(h) || keep(h))
      .map((h) => {
        if (!h.sections) return h;
        const sections = h.sections
          .map((s) => ({ ...s, files: s.files.filter(keep) }))
          .filter((s) => s.files.length > 0);
        return { ...h, sections };
      })
      .filter((h) => !isFolderHit(h) || (h.sections && h.sections.length > 0) || !h.site);

    return { visibleHits: deduped, dupKeys };
  }

  // ── Render ───────────────────────────────────────────────────────────────

  const info = jvidData?.info ?? null;
  const netRows = jvidData?.network ?? [];
  const orders = jvidData?.orders ?? [];
  const subs = jvidData?.subscriptions ?? [];
  const ssvTsa = jvidData?.ssvTsa ?? [];
  const isEthernet = subs[0]?.type === 'B2B_UTAC';

  const operatorOrderIds = isExternal ? new Set(orders.map((o) => String(o.orderId))) : null;
  const scopedDocsHits = operatorOrderIds
    ? docsHits
        .map((h) =>
          h.sections
            ? {
                ...h,
                sections: h.sections.filter((s) => operatorOrderIds.has(s.name.match(/^(\d+)/)?.[1] ?? '')),
              }
            : h,
        )
        .filter((h) => !h.sections || h.sections.length > 0)
    : docsHits;
  const { visibleHits, dupKeys } = buildVisibleHits(scopedDocsHits);

  // Azure section is internal-only, so unlike operatorOrderIds above this
  // isn't about external-operator isolation — it's the same "only show real
  // orders on this jvid" filter, dropping any stray/orphaned Azure folder
  // that doesn't match a known order id.
  const knownOrderIds = new Set(orders.map((o) => String(o.orderId)));
  const visibleAzureSections = azureSections
    .filter((s) => knownOrderIds.has(s.orderId))
    .filter((s) => s.files.length > 0)
    .sort((a, b) => b.name.localeCompare(a.name, undefined, { numeric: true }));

  // Deepomatic only gives a per-item hasDuplicate boolean, no group id — the
  // best available grouping key for "which items are duplicates of each
  // other" is workItemName (the shot type/label). Within a flagged group,
  // keep only the earliest inputCreateDate.
  function dedupeDeepomaticItems(items: DeepomaticJob['workItems']): DeepomaticJob['workItems'] {
    const groups = new Map<string, DeepomaticJob['workItems']>();
    for (const item of items) {
      if (!item.hasDuplicate || !item.workItemName) continue;
      if (!groups.has(item.workItemName)) groups.set(item.workItemName, []);
      groups.get(item.workItemName)!.push(item);
    }
    const dropIds = new Set<string>();
    for (const group of groups.values()) {
      if (group.length < 2) continue;
      const sorted = [...group].sort((a, b) => {
        const ta = a.inputCreateDate ? new Date(a.inputCreateDate).getTime() : Infinity;
        const tb = b.inputCreateDate ? new Date(b.inputCreateDate).getTime() : Infinity;
        return ta - tb;
      });
      for (const item of sorted.slice(1)) dropIds.add(item.inputId);
    }
    return items.filter((item) => !dropIds.has(item.inputId));
  }

  function docLink(token: string | null | undefined) {
    if (!token) return null;
    return (
      <a
        href={`${API_PREFIX}/address-info/shared-download?token=${encodeURIComponent(token)}`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center text-brand-rose hover:underline"
        title="Open document"
      >
        <FileText className="w-4 h-4" />
      </a>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800">{t('addressInfo.title')}</h1>
        <p className="text-sm text-slate-500 mt-1">{t('addressInfo.subtitle')}</p>
      </div>

      {/* Search toolbar — styled like ticket list */}
      <div className="bg-white border border-slate-200 rounded-xl">
        <div className="px-6 py-4 flex flex-col gap-3">
          {/* Address text search */}
          <div className="relative w-full">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              placeholder={t('addressInfo.searchPlaceholder')}
              value={addressQuery}
              onChange={(e) => setAddressQuery(e.target.value)}
              autoComplete="off"
              className="w-full pl-10 pr-9 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 transition-all placeholder:text-slate-400"
            />
            {addressLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />}
            {!addressLoading && addressQuery && (
              <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                onClick={() => { setAddressQuery(''); setSuggestions([]); }}>
                <X className="w-4 h-4" />
              </button>
            )}
            {suggestions.length > 0 && (
              <ul ref={suggestionsRef} className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                {suggestions.map((s) => (
                  <li key={s.jvid}>
                    <button
                      type="button"
                      className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0"
                      onClick={() => selectSuggestion(s)}
                    >
                      <span className="font-mono text-xs text-slate-400 mr-2">{s.jvid}</span>
                      <span className="font-medium text-slate-800">{s.municipality} {s.street_name}{s.house_number_extension ? ' ' + s.house_number_extension : ''}{s.bus_number ? ' bus ' + s.bus_number : ''}</span>
                      <span className="text-slate-400 ml-1 text-xs">{s.postal_code} {s.city}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* JVID direct input */}
          <div className="relative w-52 self-start">
            <input
              type="text"
              inputMode="numeric"
              placeholder={t('addressInfo.jvidPlaceholder')}
              value={jvidInput}
              maxLength={8}
              onChange={(e) => setJvidInput(e.target.value.replace(/\D/g, '').slice(0, 8))}
              className="w-full px-4 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 transition-all placeholder:text-slate-400 font-mono"
            />
            {jvidInput.length > 0 && jvidInput.length < 8 && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">{jvidInput.length}/8</span>
            )}
          </div>

          {/* UTAC search — auto-triggers when 18 valid digits entered */}
          <div className="flex items-center gap-2 self-start">
            <div className="relative w-52">
              <input
                type="text"
                inputMode="numeric"
                placeholder={t('addressInfo.utacPlaceholder')}
                value={utacInput}
                maxLength={18}
                onChange={(e) => { setUtacInput(e.target.value.replace(/\D/g, '').slice(0, 18)); setUtacError(null); }}
                className="w-full px-4 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 transition-all placeholder:text-slate-400 font-mono"
              />
              {utacLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />}
              {!utacLoading && utacInput.length > 0 && utacInput.length < 18 && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">{utacInput.length}/18</span>
              )}
            </div>
            {utacError && <span className="text-xs text-red-500">{utacError}</span>}
          </div>
        </div>
      </div>

      {/* Loading / error state */}
      {jvidLoading && (
        <div className="space-y-3">
          <Skeleton className="h-6 w-1/3" />
          <Skeleton className="h-72 w-full" />
          <Skeleton className="h-6 w-2/3" />
        </div>
      )}
      {jvidError && <p className="text-sm text-red-500">{jvidError}</p>}

      {/* Results */}
      {!jvidLoading && jvid && jvidData && (
        <Accordion type="multiple" defaultValue={['address', 'network', 'orders', 'subscriptions', 'ssvtsa', 'documents', 'deepomatic', 'azure']} className="space-y-2">

          {/* Address details + map side by side */}
          {info && (
            <AccordionItem value="address" className="rounded-lg overflow-hidden border border-slate-200">
              <AccordionTrigger className="gradient-band text-white text-sm font-semibold uppercase tracking-wide px-4 py-3 hover:no-underline [&>svg]:text-white">{t('addressInfo.sections.address')}</AccordionTrigger>
              <AccordionContent className="pt-3">
                <div className="pb-3 flex flex-col xl:flex-row gap-4">
                  <div className="flex-1 min-w-0">
                    <Row label="JVID" value={info.jvid} />
                    <Row label={t('addressInfo.fields.street')} value={[info.streetName, info.houseNumberExtension, info.busNumber].filter(Boolean).join(' ')} />
                    <Row label={t('addressInfo.fields.city')} value={[info.municipality, info.city].filter(Boolean).join(', ')} />
                    <Row label={t('addressInfo.fields.postalCode')} value={info.postalCode} />
                    {info.areaName != null && <Row label={t('addressInfo.fields.area')} value={[info.areaName, info.areaId].filter(Boolean).join(' ')} />}
                    <Row label={t('addressInfo.fields.centralPop')} value={info.centralPop} />
                    <Row label={t('addressInfo.fields.areaPop')} value={info.areaPop} />
                    {!isExternal && info.deliveryStatusCode != null && <Row label={t('addressInfo.fields.deliveryCode')} value={info.deliveryStatusCode} />}
                    {isExternal && info.deliveryStatusOperator != null && <Row label={t('addressInfo.fields.deliveryCode')} value={info.deliveryStatusOperator} />}
                    {info.deliveryStatus != null && <Row label={t('addressInfo.fields.delivery')} value={info.deliveryStatus} />}
                    {info.deliveryStatusCategory != null && <Row label={t('addressInfo.fields.deliveryCategory')} value={info.deliveryStatusCategory} />}
                    {info.homeReleased != null && <Row label={t('addressInfo.fields.homeReleased')} value={info.homeReleased ? 'Yes' : 'No'} />}
                    {info.quoteRequired != null && <Row label={t('addressInfo.fields.quoteRequired')} value={info.quoteRequired} />}
                    {info.status != null && <Row label={t('addressInfo.fields.status')} value={info.status} />}
                    {info.quadrant != null && <Row label={t('addressInfo.fields.quadrant')} value={info.quadrant} />}
                  </div>
                  <div className="xl:w-[48rem] shrink-0 overflow-hidden rounded-lg">
                    {(() => {
                      const lat = (info as any)?.lat ?? null;
                      const lon = (info as any)?.lon ?? null;
                      if (lat == null || lon == null) {
                        return <div className="h-full min-h-48 flex items-center justify-center rounded-lg bg-slate-50 text-sm text-slate-400">{t('addressInfo.noCoordinates')}</div>;
                      }
                      const label = [info?.streetName, info?.houseNumberExtension].filter(Boolean).join(' ') || jvid;
                      return (
                        <Suspense fallback={<Skeleton className="h-48 w-full" />}>
                          <AddressInfoMap lat={lat} lon={lon} label={label} showCocon={canViewNetwork} />
                        </Suspense>
                      );
                    })()}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          )}

          {/* Network — ODF/Patch Panel always visible; Splitter only when operator matches. A JVID can have >1 row (e.g. two fibers). */}
          {netRows.length > 0 && (
            <AccordionItem value="network" className="rounded-lg overflow-hidden border border-slate-200">
              <AccordionTrigger className="gradient-band text-white text-sm font-semibold uppercase tracking-wide px-4 py-3 hover:no-underline [&>svg]:text-white">
                {isEthernet ? t('addressInfo.sections.ethernet') : t('addressInfo.sections.network')}
              </AccordionTrigger>
              <AccordionContent className="pt-3">
                <div className="pb-3 space-y-4">
                  {/* PoP — the reason this section is consulted: PCD picks the right central or area
                      PoP from here. Sourced from REPORTING_ADDRESS (the only view carrying both) and
                      constant per JVID, so it sits above the per-fiber tables rather than in them. */}
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">PoP</p>
                    <div className="border border-slate-200 rounded-lg px-3 py-1">
                      <Row label={t('addressInfo.fields.centralPop')} value={info?.centralPop} />
                      <Row label={t('addressInfo.fields.areaPop')} value={info?.areaPop} />
                    </div>
                  </div>
                  {/* Splitter table — operator-restricted, one row per fiber */}
                  {(() => {
                    const splitterRows = netRows.filter(
                      (net) => !isExternal || net.operator?.toLowerCase() === user?.operatorName?.toLowerCase(),
                    );
                    return splitterRows.length > 0 && (
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">Splitter</p>
                        <div className="border border-slate-200 rounded-lg overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b border-slate-200 bg-slate-50">
                                {['Operator','Reference','Rack','Height','Label','Port','Port Status','Order','Patch'].map(h => (
                                  <th key={h} className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400 whitespace-nowrap">{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {splitterRows.map((net, i) => (
                                <tr key={i} className={i > 0 ? 'border-t border-slate-100' : undefined}>
                                  <td className="px-3 py-2 text-slate-700">{net.operator ?? DASH}</td>
                                  <td className="px-3 py-2 text-slate-700 font-mono">{net.customerReference ?? DASH}</td>
                                  <td className="px-3 py-2 text-slate-700">{net.splitterRackLabel ?? DASH}</td>
                                  <td className="px-3 py-2 text-slate-700">{net.splitterHeight ?? DASH}</td>
                                  <td className="px-3 py-2 text-slate-700">{net.splitterLabel ?? DASH}</td>
                                  <td className="px-3 py-2 text-slate-700">{net.splitterPortPosition ?? DASH}</td>
                                  <td className="px-3 py-2 text-slate-700">{net.splitterPortStatus ?? DASH}</td>
                                  <td className="px-3 py-2 text-slate-700">{net.splitterPortOrder ?? DASH}</td>
                                  <td className="px-3 py-2 text-slate-700">{net.splitterPatchStatus ?? DASH}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}
                  {/* ODF / Patch Panel table — one row per fiber */}
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">ODF / Patch Panel</p>
                    <div className="border border-slate-200 rounded-lg overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-slate-200 bg-slate-50">
                            {['Rack','Height','Port','Port Status','Fiber #','UTAC'].map(h => (
                              <th key={h} className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400 whitespace-nowrap">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {netRows.map((net, i) => (
                            <tr key={i} className={i > 0 ? 'border-t border-slate-100' : undefined}>
                              <td className="px-3 py-2 text-slate-700">{net.patchPanelRackLabel ?? DASH}</td>
                              <td className="px-3 py-2 text-slate-700">{net.patchPanelHeight ?? DASH}</td>
                              <td className="px-3 py-2 text-slate-700">{net.patchPanelPortPosition ?? DASH}</td>
                              <td className="px-3 py-2 text-slate-700">{net.patchPanelPortStatus ?? DASH}</td>
                              <td className="px-3 py-2 text-slate-700">{net.addressFiberNumber ?? DASH}</td>
                              <td className="px-3 py-2 text-slate-700 font-mono">{net.utac ?? DASH}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          )}

          {/* Orders */}
          {orders.length > 0 && (
            <AccordionItem value="orders" className="rounded-lg overflow-hidden border border-slate-200">
              <AccordionTrigger className="gradient-band text-white text-sm font-semibold uppercase tracking-wide px-4 py-3 hover:no-underline [&>svg]:text-white">{t('addressInfo.sections.orders')} ({orders.length})</AccordionTrigger>
              <AccordionContent className="pt-3">
                <div className="border border-slate-200 rounded-lg overflow-x-auto mb-3">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">ID</th>
                        <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{t('addressInfo.cols.type')}</th>
                        <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{t('addressInfo.cols.shortStatus')}</th>
                        <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{t('addressInfo.cols.status')}</th>
                        <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{t('addressInfo.cols.splitter')}</th>
                        <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{t('addressInfo.cols.created')}</th>
                        <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{t('addressInfo.cols.executed')}</th>
                        <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{t('addressInfo.cols.appointment')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {orders.map((o) => {
                        const isOpen = expandedOrder === o.orderId;
                        const wf = workflows[o.orderId];
                        return (
                          <Fragment key={o.orderId}>
                            <tr
                              role="button" tabIndex={0} aria-expanded={isOpen}
                              onClick={() => toggleOrder(o.orderId)}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleOrder(o.orderId); } }}
                              className={`cursor-pointer hover:bg-slate-50 transition-colors ${isOpen ? 'bg-brand-rose/5' : ''}`}
                            >
                              <td className="px-3 py-2 text-slate-700 whitespace-nowrap">
                                <ChevronRight className={`inline w-3 h-3 mr-1 text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                                {o.orderId}
                                {o.customerRef && <span className="text-slate-400 ml-1">· {o.customerRef}</span>}
                              </td>
                              <td className="px-3 py-2 text-slate-700">{o.orderType || DASH}</td>
                              <td className="px-3 py-2 text-slate-700">{shortOrderStatus(o.orderType, o.orderStatus)}</td>
                              <td className="px-3 py-2 text-slate-700">{o.orderStatus || DASH}</td>
                              <td className="px-3 py-2 text-slate-700">{o.splitterRef ?? DASH}</td>
                              <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{fmtDate(o.createdAt)}</td>
                              <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{fmtDate(o.technicallyExecutedAt)}</td>
                              <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{o.appointment ?? DASH}</td>
                            </tr>
                            {isOpen && (
                              <tr>
                                <td colSpan={8} className="px-3 py-2 bg-slate-50/60">
                                  {wf === 'loading' || wf === undefined
                                    ? <div className="flex items-center gap-2 text-xs text-slate-400 py-2"><Loader2 className="w-3.5 h-3.5 animate-spin" />{t('addressInfo.loading')}</div>
                                    : wf === 'error'
                                    ? <p className="text-xs text-red-500 py-2">{t('addressInfo.workflowError')}</p>
                                    : wf.length === 0
                                    ? <p className="text-xs text-slate-400 py-2">{t('addressInfo.workflowNone')}</p>
                                    : (
                                      <ol className="space-y-1.5 py-1">
                                        {wf.map((e, i) => (
                                          <li key={i} className="flex gap-3 text-xs">
                                            <span className="text-slate-400 whitespace-nowrap w-24 shrink-0">{fmtDate(e.updatedAt)}</span>
                                            <span className="text-slate-700">
                                              <span className="font-medium">{e.fromStatus ? `${e.fromStatus} → ` : ''}{e.toStatus}</span>
                                              {e.timeslot && <span className="text-slate-400 ml-2">{e.timeslot}</span>}
                                              {e.delayReason && <span className="block text-amber-600">{e.delayReason}{e.delayReasonRemark ? ` — ${e.delayReasonRemark}` : ''}</span>}
                                              {/* DMP text shown verbatim; the server sends it to internal users and to operators whose switch is on. */}
                                              {e.delayReasonInternalRemark && (
                                                <span className="block text-slate-600 whitespace-pre-wrap break-words">
                                                  <span className="font-medium">{t('addressInfo.workflowRemark')}:</span> {e.delayReasonInternalRemark}
                                                </span>
                                              )}
                                            </span>
                                          </li>
                                        ))}
                                      </ol>
                                    )
                                  }
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </AccordionContent>
            </AccordionItem>
          )}

          {/* Subscriptions */}
          {subs.length > 0 && (
            <AccordionItem value="subscriptions" className="rounded-lg overflow-hidden border border-slate-200">
              <AccordionTrigger className="gradient-band text-white text-sm font-semibold uppercase tracking-wide px-4 py-3 hover:no-underline [&>svg]:text-white">{t('addressInfo.sections.subscriptions')} ({subs.length})</AccordionTrigger>
              <AccordionContent className="pt-3">
                <div className="border border-slate-200 rounded-lg overflow-x-auto mb-3">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{t('addressInfo.cols.operator')}</th>
                        <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{t('addressInfo.cols.type')}</th>
                        <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Identifier</th>
                        <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">SLA</th>
                        <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{t('addressInfo.cols.start')}</th>
                        <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{t('addressInfo.cols.end')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {subs.map((s, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2 text-slate-700">{s.operator ?? DASH}</td>
                          <td className="px-3 py-2 text-slate-700">{s.type ?? DASH}</td>
                          <td className="px-3 py-2 text-slate-700 font-mono">{s.subscriptionIdentifier ?? DASH}</td>
                          <td className="px-3 py-2 text-slate-700">{s.sla ?? DASH}</td>
                          <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{fmtDate(s.startDate)}</td>
                          <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{fmtDate(s.endDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </AccordionContent>
            </AccordionItem>
          )}

          {/* SSV / TSA — internal only */}
          {ssvTsa.length > 0 && !isExternal && (
            <AccordionItem value="ssvtsa" className="rounded-lg overflow-hidden border border-slate-200">
              <AccordionTrigger className="gradient-band text-white text-sm font-semibold uppercase tracking-wide px-4 py-3 hover:no-underline [&>svg]:text-white">{t('addressInfo.sections.ssvTsa')} ({ssvTsa.length})</AccordionTrigger>
              <AccordionContent className="pt-3">
                <div className="border border-slate-200 rounded-lg overflow-x-auto mb-3">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Task</th>
                        <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">SSV Status</th>
                        <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">SSV Doc</th>
                        <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">TSA Status</th>
                        <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">TSA Signature</th>
                        <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">TSA Signed</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {ssvTsa.map((r, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2 text-slate-700 font-mono">{r.task || DASH}</td>
                          <td className="px-3 py-2 text-slate-700">{r.ssvStatus || DASH}</td>
                          <td className="px-3 py-2">{docLink(r.ssv_doc_token)}</td>
                          <td className="px-3 py-2 text-slate-700">{r.tsaStatus || DASH}</td>
                          <td className="px-3 py-2">{docLink(r.tsa_doc_for_signature_token)}</td>
                          <td className="px-3 py-2">{docLink(r.tsa_doc_signed_token)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </AccordionContent>
            </AccordionItem>
          )}

          {/* SharePoint Documents */}
          <AccordionItem value="documents" className="rounded-lg overflow-hidden border border-slate-200">
            <AccordionTrigger className="gradient-band text-white text-sm font-semibold uppercase tracking-wide px-4 py-3 hover:no-underline [&>svg]:text-white">
              <span className="flex items-center gap-2">
                {t('addressInfo.sections.documents')}
                {!docsLoading && visibleHits.length > 0 && <span className="text-white/70 font-normal normal-case tracking-normal text-xs">({visibleHits.length})</span>}
              </span>
            </AccordionTrigger>
            <AccordionContent className="pt-3">
              <div className="pb-3">
                {docsLoading && <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin" />{t('addressInfo.docsSearching')}</div>}
                {docsError && <p className="text-sm text-red-500">{docsError}</p>}
                {!docsLoading && !docsError && visibleHits.length === 0 && (
                  <p className="text-sm text-slate-400">{t('addressInfo.docsNone')}</p>
                )}
                {!docsLoading && visibleHits.length > 0 && (
                  <ul className="space-y-4">
                    {visibleHits.map((h) => {
                      const icon = iconFor(h.name);
                      const isFolder = icon === '📁';
                      return (
                        <li key={h.id} className="border border-slate-100 rounded-lg p-3">
                          {/* Hit header */}
                          {isFolder ? (
                            <div className="flex items-center gap-2 font-medium text-sm text-slate-700 mb-2">
                              <span>{icon}</span> <span>{h.name}</span>
                            </div>
                          ) : (
                            <a
                              href={`${API_PREFIX}/address-info/download?driveId=${encodeURIComponent(h.driveId || '')}&itemId=${encodeURIComponent(h.itemId || '')}&_token=${encodeURIComponent(getAccessToken() || '')}`}
                              className="flex items-center gap-2 font-medium text-sm text-brand-rose hover:underline mb-1"
                            >
                              <span>{icon}</span> <span>{h.name}</span>
                            </a>
                          )}
                          {h.summary && <p className="text-xs text-slate-500 mb-2">{renderSummary(h.summary)}</p>}
                          {!isFolder && <p className="text-xs text-slate-400">{fmtDate(h.lastModified)}{h.size ? ` · ${formatFileSize(h.size)}` : ''}</p>}

                          {/* Folder sections with file tiles */}
                          {(h.sections || []).length > 0 && (() => {
                            const keep = (f: DriveFile) => !(f.site === 'OrderManagement' && f.hash && dupKeys.has(f.hash));
                            const visible = (h.sections || [])
                              .map((sec) => ({ sec, files: sec.files.filter(keep) }))
                              .filter(({ files }) => files.length > 0);
                            if (visible.length === 0) return null;
                            return (
                              <div className="mt-2 space-y-3">
                                {visible.map(({ sec, files }) => {
                                  const sorted = [...files].sort((a, b) => (isImage(b.name) ? 1 : 0) - (isImage(a.name) ? 1 : 0));
                                  const gallery = sorted
                                    .filter((f) => isImage(f.name) && f.hasThumbnail)
                                    .map((f) => ({
                                      src: `${API_PREFIX}/address-info/thumbnail?driveId=${encodeURIComponent(f.driveId || '')}&itemId=${encodeURIComponent(f.itemId || '')}&size=large`,
                                      name: f.name,
                                    }));
                                  return (
                                    <div key={sec.itemId}>
                                      <div className="flex items-center justify-between mb-1.5">
                                        <span className="text-xs font-semibold text-slate-500">📁 {sec.name || '(this folder)'}</span>
                                        <span className="text-xs text-slate-400">{files.length} file{files.length === 1 ? '' : 's'}</span>
                                      </div>
                                      <div className="flex flex-wrap gap-2">
                                        {sorted.map((f) => renderFileTile(f, dupKeys, gallery))}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Deepomatic field photos — internal only */}
          {!isExternal && (
            <AccordionItem value="deepomatic" className="rounded-lg overflow-hidden border border-slate-200">
              <AccordionTrigger className="gradient-band text-white text-sm font-semibold uppercase tracking-wide px-4 py-3 hover:no-underline [&>svg]:text-white">
                <span className="flex items-center gap-2">
                  {t('addressInfo.sections.deepomatic')}
                  {!deepomaticLoading && deepomaticJobs.length > 0 && <span className="text-white/70 font-normal normal-case tracking-normal text-xs">({deepomaticJobs.length})</span>}
                </span>
              </AccordionTrigger>
              <AccordionContent className="pt-3">
                <div className="pb-3">
                  {deepomaticLoading && <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin" />{t('addressInfo.deepomaticSearching')}</div>}
                  {deepomaticError && <p className="text-sm text-red-500">{deepomaticError}</p>}
                  {!deepomaticLoading && !deepomaticError && deepomaticJobs.length === 0 && (
                    <p className="text-sm text-slate-400">{t('addressInfo.deepomaticNone')}</p>
                  )}
                  {!deepomaticLoading && deepomaticJobs.length > 0 && (
                    <ul className="space-y-4">
                      {deepomaticJobs.map((job) => (
                        <li key={job.id} className="border border-slate-100 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-sm text-slate-700">{job.name}</span>
                            <span className="text-xs text-slate-400">{fmtDate(job.createDate)}</span>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 mb-3">
                            <MetaField label={t('addressInfo.deepomatic.area')} value={job.area} />
                            <MetaField label={t('addressInfo.deepomatic.address')} value={job.address} />
                            <MetaField label={t('addressInfo.deepomatic.technician')} value={job.technician} />
                            <MetaField label={t('addressInfo.deepomatic.subcontractor')} value={job.subcontractor} />
                            <MetaField label={t('addressInfo.deepomatic.fiberColor')} value={job.fiberColor} />
                            <MetaField label={t('addressInfo.deepomatic.workOrderType')} value={job.workOrderType} />
                            <MetaField label={t('addressInfo.deepomatic.progressScore')} value={job.progressScore != null ? `${Math.round(job.progressScore)}%` : null} />
                            <MetaField label={t('addressInfo.deepomatic.corrected')} value={job.hasBeenCorrected == null ? null : (job.hasBeenCorrected ? '✓' : '✗')} />
                          </div>
                          {job.workItems.length > 0 && (() => {
                            const items = dedupeDeepomaticItems(job.workItems);
                            const gallery = items
                              .filter((item) => !missingDeepomaticImages.has(item.inputId))
                              .map((item) => ({
                                src: `${API_PREFIX}/address-info/deepomatic/image/${encodeURIComponent(`${item.inputId}.${item.fileExtension || 'jpg'}`)}`,
                                name: item.workItemName || item.inputId,
                              }));
                            return (
                              <div className="flex flex-wrap gap-2">
                                {items.map((item) => renderDeepomaticTile(item, gallery))}
                              </div>
                            );
                          })()}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}

          {/* Order-image Azure Blob files — internal only */}
          {!isExternal && (
            <AccordionItem value="azure" className="rounded-lg overflow-hidden border border-slate-200">
              <AccordionTrigger className="gradient-band text-white text-sm font-semibold uppercase tracking-wide px-4 py-3 hover:no-underline [&>svg]:text-white">
                <span className="flex items-center gap-2">
                  {t('addressInfo.sections.azure')}
                  {!azureLoading && visibleAzureSections.length > 0 && (
                    <span className="text-white/70 font-normal normal-case tracking-normal text-xs">
                      ({visibleAzureSections.reduce((n, s) => n + s.files.length, 0)})
                    </span>
                  )}
                </span>
              </AccordionTrigger>
              <AccordionContent className="pt-3">
                <div className="pb-3">
                  {azureLoading && <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin" />{t('addressInfo.azureSearching')}</div>}
                  {azureError && <p className="text-sm text-red-500">{azureError}</p>}
                  {!azureLoading && !azureError && visibleAzureSections.length === 0 && (
                    <p className="text-sm text-slate-400">{t('addressInfo.azureNone')}</p>
                  )}
                  {!azureLoading && visibleAzureSections.length > 0 && (
                    <div className="space-y-3">
                      {visibleAzureSections.map((sec) => {
                        const sorted = [...sec.files].sort((a, b) => (isImage(b.name) ? 1 : 0) - (isImage(a.name) ? 1 : 0));
                        const gallery = sorted
                          .filter((f) => isImage(f.name))
                          .map((f) => ({
                            src: `${API_PREFIX}/address-info/azure-image?path=${encodeURIComponent(f.path)}`,
                            name: f.name,
                          }));
                        return (
                          <div key={sec.name}>
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-xs font-semibold text-slate-500">📁 {sec.name}</span>
                              <span className="text-xs text-slate-400">{sec.files.length} file{sec.files.length === 1 ? '' : 's'}</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {sorted.map((f) => renderAzureFileTile(f, gallery))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}

        </Accordion>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <div
            className={`relative bg-white rounded-xl overflow-hidden shadow-2xl ${lightbox.kind === 'iframe' ? 'w-full max-w-4xl h-[80vh]' : 'max-w-3xl'}`}
            onClick={(e) => e.stopPropagation()}
          >
            {lightbox.gallery && lightbox.gallery.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }}
                  className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors z-10"
                  aria-label="Previous"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }}
                  className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors z-10"
                  aria-label="Next"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
            {lightbox.kind === 'iframe' ? (
              <iframe
                className="w-full h-full border-0"
                src={lightbox.src}
                title={lightbox.name}
              />
            ) : (
              <div className="max-h-[80vh] max-w-full overflow-hidden">
                <AuthImg
                  src={lightbox.src}
                  alt={lightbox.name}
                  className="max-h-[80vh] max-w-full object-contain"
                  style={{
                    transform: `scale(${imgZoom})`,
                    transformOrigin: imgZoomOrigin,
                    transition: 'transform 0.15s ease-out',
                    cursor: imgZoom > 1 ? 'zoom-out' : 'zoom-in',
                  }}
                  onClick={handleImageZoomClick}
                  onWheel={handleImageZoomWheel}
                />
              </div>
            )}
            <div className="absolute top-3 right-3">
              <button
                type="button"
                onClick={() => setLightbox(null)}
                className="w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
